// =============================================================================
// useMobileFilters.ts
// Derived data for the mobile shell — keeps MobileLayout slim.
//
// Two outputs:
//  1. `dateRangedOrders` + `rangedCounts` — orders pre-filtered by the shared
//     dateRange (Dashboard list + stat cards depend on both).
//  2. `filteredInvLog` — inventory log filtered by the History-only date
//     range (Items view ignores the range; it's "right now" stock state).
// =============================================================================

import { useMemo } from 'react';

import type { Order, OrderCounts, InventoryLogEntry } from '../../../lib/types';
import type { DateRange } from '../sheets/DateRangePicker';

const isInRange = (ymdValue: string, range: DateRange): boolean => {
  if (range.from && ymdValue < range.from) return false;
  if (range.to   && ymdValue > range.to)   return false;
  return true;
};

const isAllTime = (range: DateRange): boolean =>
  range.value === 'all' || (!range.from && !range.to);

export function useMobileDateFilters(
  allOrders: Order[],
  serverCounts: OrderCounts,
  orderRange: DateRange,
  invLog: InventoryLogEntry[],
  invLogRange: DateRange,
) {
  // Dashboard: orders pre-filtered by the shared range.
  const dateRangedOrders = useMemo(() => {
    if (isAllTime(orderRange)) return allOrders;
    return allOrders.filter(o => isInRange((o.created_at || '').slice(0, 10), orderRange));
  }, [allOrders, orderRange.value, orderRange.from, orderRange.to]);

  // Stat cards reflect the date-filtered set so all four numbers and the list
  // below them stay in sync. Falls back to server counts when "All time" is
  // selected (avoids a recompute that produces the same result).
  const rangedCounts = useMemo<OrderCounts>(() => {
    if (isAllTime(orderRange)) return serverCounts;
    const c: OrderCounts = { pending: 0, ordered: 0, delivered: 0, closed: 0, total: 0 };
    for (const o of dateRangedOrders) {
      const k = (o.status || '').toLowerCase() as keyof Omit<OrderCounts, 'total'>;
      if (k in c) (c as any)[k] += 1;
      c.total += 1;
    }
    return c;
  }, [dateRangedOrders, serverCounts, orderRange.value, orderRange.from, orderRange.to]);

  // Inventory History: log entries filtered by their own range (different
  // time-axis: log timestamps vs order created_at).
  const filteredInvLog = useMemo(() => {
    if (isAllTime(invLogRange)) return invLog;
    return invLog.filter(e => isInRange((e.timestamp || '').slice(0, 10), invLogRange));
  }, [invLog, invLogRange.value, invLogRange.from, invLogRange.to]);

  return { dateRangedOrders, rangedCounts, filteredInvLog };
}
